import { Component, Inject, OnInit, ViewChild } from "@angular/core";
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import {
  MAT_DIALOG_DATA,
  MatAutocompleteSelectedEvent,
  MatDialog,
  MatDialogRef,
  MatOption,
  MatSnackBar,
  MatStepper,
} from "@angular/material";
import { TranslateService } from "@ngx-translate/core";
import { User } from "app/shared/models/users.model";
import * as moment from "moment";
import { Observable, of, zip } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { FuseTranslationLoaderService } from "../../../../../../@fuse/services/translation-loader.service";
import { AuthService } from "../../../../../services/auth.service";
import { CompaniesService } from "../../../../../services/companies.service";
import { CompanyBranchesService } from "../../../../../services/company-branches.service";
import { CompanyCarsService } from "../../../../../services/company-cars.service";
import { FilteredListsService } from "../../../../../services/filtered-lists.service";
import { OrdersService } from "../../../../../services/orders.service";
import { TasksService } from "../../../../../services/tasks.service";
import { UsersService } from "../../../../../services/users.service";
import { Mode } from "../../../../../shared/consts/mode";
import { OrderTypes } from "../../../../../shared/consts/orders";
import {
  EndTimeOptions,
  StartTimeOptions,
} from "../../../../../shared/consts/tasks";
import { CustomerCompanyBranch } from "../../../../../shared/models/company-branches.model";
import { CustomerCompanyCar } from "../../../../../shared/models/company-cars.model";
import { CompanyBranch } from "../../../../../shared/models/company.model";
import {
  FilteredCompany,
  FilteredFraction,
  FilteredTransport,
} from "../../../../../shared/models/filtered-items.model";
import { ItemsOrigins } from "../../../../../shared/models/history.model";
import { RequestParams } from "../../../../../shared/models/list.model";
import { OrderExport } from "../../../../../shared/models/orders.model";
import {
  OrderExportSubform,
  TaskExport,
  TaskStatuses,
  TaskTypes,
} from "../../../../../shared/models/tasks.model";
import { getDaysInMonth } from "../../../../../shared/utils/custom-date-formatter";
import { markFormGroupTouched } from "../../../../../shared/utils/markFormGroupAsTouched";
import { mapDatetimeToUtc } from "../../../../../shared/utils/tasks-map";
import { StartEndDateValidator } from "../../../../../shared/validators/startEndDate.validator";
import { locale as english } from "../../../../../translations/en";
import { locale as polish } from "../../../../../translations/pl";
import { CompanyEditorComponent } from "../../../../companies/components/company-editor/company-editor.component";
import { ConfirmModalComponent } from "../../../../shared/components/confirm-modal/confirm-modal.component";
import { HistoryDialogComponent } from "../../../../shared/components/history-dialog/history-dialog.component";

@Component({
  selector: "app-task-export-form",
  templateUrl: "./task-export-form.component.html",
  styleUrls: ["./task-export-form.component.scss"],
})
export class TaskExportFormComponent implements OnInit {
  @ViewChild("companySelect") companySelect;
  @ViewChild("stepper") stepper: MatStepper;
  isExpanded: boolean[] = [];
  public indexStep: number;
  public taskExportForm: FormGroup;
  public taskId: TaskExport["uuid"] | TaskExport["taskNumber"];
  public task: TaskExport;
  public customerCompanyBranches: {
    [key: string]: CustomerCompanyBranch[];
  } = {};
  public taskStatuses = TaskStatuses;
  public taskTypes = TaskTypes;
  public fractionOptions: FilteredFraction[] = [];
  public transportOptions: FilteredTransport[] = [];
  public customerCompanyOptions: FilteredCompany[] = [];
  public forwarderCompanies: FilteredCompany[] = [];
  public companyCars: CustomerCompanyCar[] = [];
  public companyBranches: CompanyBranch[] = [];
  public ordersOptions: OrderExport[] = [];
  public ordersOptionsInitial: string[] = [];
  public ordersOptionsRemoved: OrderExport[] = [];
  public startTimeOptions = StartTimeOptions;
  public endTimeOptions = EndTimeOptions;
  public mode: Mode;
  public user: User;
  public isLoading = false;
  public searchCustomerCompany: FormControl;
  public searchTransport: FormControl;
  public searchFractions: FormControl;
  public searchForwarder: FormControl;
  public companyCarSelect: FormControl;
  public forwarderIdSelect: FormControl;
  public searchOrders: FormControl;
  public todayDate: Date;
  public rangeDateEndMax: Date;
  public rangeDateEndMin: Date;
  public pdfBranch: any;
  public pdfForwarder: any;
  private filterParams: RequestParams = {
    page: 1,
    limit: 20,
    sort: "name",
    order: "asc",
  };
  private filterParamsFullList: RequestParams = {
    page: 1,
    limit: 1000,
    sort: "name",
    order: "asc",
    active: "true",
  };
  private filterOrdersParams: RequestParams = {
    page: 1,
    limit: 20,
    sort: "createdDate",
    order: "asc",
    available: true,
    orderType: this.taskTypes.Export,
    task: false,
    isReady: true,
  };
  private filterForwarderParams: RequestParams = {
    page: 1,
    limit: 20,
    sort: "createdDate",
    order: "asc",
    companyType: "forwarder",
  };

  constructor(
    public taskEditorDialogRef: MatDialogRef<TaskExportFormComponent>,
    @Inject(MAT_DIALOG_DATA) private data: any,
    private matDialog: MatDialog,
    private authService: AuthService,
    private tasksService: TasksService,
    private ordersService: OrdersService,
    private filteredListsService: FilteredListsService,
    private usersService: UsersService,
    private translateService: TranslateService,
    private companiesService: CompaniesService,
    private companyBranchesService: CompanyBranchesService,
    private companyCarsService: CompanyCarsService,
    private snackBar: MatSnackBar,
    private formBuilder: FormBuilder,
    private _fuseTranslationLoaderService: FuseTranslationLoaderService
  ) {
    this.user = this.authService.user.user;
    this.mode = data.mode;
    this.taskId = data.taskId || data.taskNumber;
    this._fuseTranslationLoaderService.loadTranslations(polish, english);
    this.indexStep = data.indexStep || 0;
  }

  ngOnInit() {
    this.createSearchFilters();
    this.createForm();

    if (this.mode === "edit" || this.mode === "view" || this.mode === "clone") {
      this.tasksService
        .getTask(this.taskId)
        .pipe(
          map((res) => {
            this.task = res;
            this.taskId = res.uuid;
            res.taskData.forEach((item) => {
              if (item.order && item.order.uuid) {
                this.ordersOptionsInitial.push(item.order.uuid);
              }
            });
            this.createForm(this.task);
            this.getPdfNeededData();
          }),
          switchMap(() => {
            return zip(
              this.filteredListsService.getFractionsList(
                this.filterParamsFullList
              ),
              this.filteredListsService.getTransportList(
                this.filterParamsFullList
              ),
              this.filteredListsService.getCompaniesList(this.filterParams),
              this.usersService.getCompanyBranchList(),
              this.filteredListsService.getOrdersList(this.filterOrdersParams),
              this.filteredListsService.getCompaniesList(
                this.filterForwarderParams
              )
            );
          })
        )
        .subscribe(
          ([
            fractionsRes,
            transportRes,
            companiesRes,
            branchesRes,
            ordersRes,
            forwarderRes,
          ]) => {
            this.fractionOptions = fractionsRes.fractions;
            this.transportOptions = transportRes.transports;
            this.customerCompanyOptions = companiesRes.customerCompanies;
            this.companyBranches = branchesRes.branches;
            this.ordersOptions = <OrderExport[]>ordersRes.orders;
            this.forwarderCompanies = forwarderRes.customerCompanies;
          }
        );
    } else {
      this.todayDate = new Date();

      zip(
        this.filteredListsService.getFractionsList(this.filterParamsFullList),
        this.filteredListsService.getTransportList(this.filterParamsFullList),
        this.filteredListsService.getCompaniesList(this.filterParams),
        this.filteredListsService.getOrdersList(this.filterOrdersParams),
        this.usersService.getCompanyBranchList(),
        this.filteredListsService.getCompaniesList(this.filterForwarderParams)
      ).subscribe(
        ([
          fractionsRes,
          transportRes,
          companiesRes,
          ordersRes,
          branchesRes,
          forwarderRes,
        ]) => {
          this.fractionOptions = fractionsRes.fractions;
          this.transportOptions = transportRes.transports;
          this.customerCompanyOptions = companiesRes.customerCompanies;
          this.ordersOptions = <OrderExport[]>ordersRes.orders;
          this.companyBranches = branchesRes.branches;
          this.forwarderCompanies = forwarderRes.customerCompanies;

          if (this.data.orders && this.data.orders.length) {
            this.createForm(this.data.orders[0]);
            this.data.orders.forEach((item, index) => {
              this.addOrderSubform(index, item, true);
            });
          }
        }
      );
    }
  }

  public submitTask() {
    this.isLoading = true;
    markFormGroupTouched(this.taskExportForm);

    if (this.taskExportForm.invalid) {
      this.isLoading = false;
      return;
    }
    const taskForm = this.taskExportForm.getRawValue();
    let taskData = taskForm.taskData.map((item) => {
      item = Object.assign({}, item);
      const id = item.customerCompanyBranch;
      item.customerCompanyBranch = Object.assign(
        {},
        item.customerCompanyBranchOther,
        { uuid: id === "other" ? null : id }
      );
      item.customerCompanyBranch.email =
        item.customerCompanyBranch.email || null;
      item.suggestedDate = item.suggestedDate || null;
      item.order.comment = item.customerCompanyBranchOther.order_comment;
      delete item.customerCompanyBranchOther;
      return item;
    });
    this.createNewOrders(taskData, taskForm.companyBranchId).subscribe(
      (taskDataFromOther) => {
        taskData = taskData.filter((item) => item.uuid !== "other");
        taskDataFromOther.forEach((item) => {
          taskData.push(item);
        });
        const newTask = {
          name: taskForm.name,
          companyBranchId: taskForm.companyBranchId,
          transportId: taskForm.transport ? taskForm.transport.uuid : null,
          startDate: mapDatetimeToUtc(taskForm.startDate, taskForm.startTime),
          endDate: mapDatetimeToUtc(taskForm.endDate, taskForm.endTime),
          taskType: taskForm.taskType,
          comment: taskForm.comment,
          referenceNumber: taskForm.referenceNumber,
          emailService: taskForm.emailService,
          registrationNumFront: taskForm.registrationNumFront,
          registrationNumBack: taskForm.registrationNumBack,
          taskData: this.mapTaskData(taskData),
          forwarderId: !!taskForm.forwarderCompanyName
            ? taskForm.forwarderId
            : null,
          forwarderCompanyName: taskForm.forwarderCompanyName,
          forwarderDriverName: taskForm.forwarderDriverName,
          forwarderDriverPhone: taskForm.forwarderDriverPhone,
          forwarderRegistrationNumberFront:
            taskForm.forwarderRegistrationNumberFront,
          forwarderRegistrationNumberBack:
            taskForm.forwarderRegistrationNumberBack,
          forwarderKilometers: Number(
            taskForm.forwarderKilometers.replace(",", ".")
          ),
          forwarderRouteDescription: taskForm.forwarderRouteDescription,
          forwarderBdo: taskForm.forwarderBdo,
          forwarderCarName: taskForm.forwarderCarName,
          forwarderCarType: taskForm.forwarderCarType,
          forwarderExpense: Number(taskForm.forwarderExpense.replace(",", ".")),
          forwarderInvoiceNumber: taskForm.forwarderInvoiceNumber,
        };

        if (this.mode === "add" || this.mode === "clone") {
          this.createTask(newTask);
        } else {
          const ordersToDelete = [];
          const oldOrdersIds =
            this.task && this.task.taskData
              ? this.task.taskData.map((item) => {
                  if (item.order && item.order.uuid) {
                    return item.order.uuid;
                  }
                })
              : [];
          const newOrdersIds = newTask.taskData
            ? newTask.taskData.map((item) => {
                if (item.orderId) {
                  return item.orderId;
                }
              })
            : [];

          oldOrdersIds.forEach((oldItem) => {
            if (oldItem && !newOrdersIds.includes(oldItem)) {
              ordersToDelete.push(oldItem);
            }
          });

          zip(
            this.tasksService.updateTask(this.taskId, newTask),
            this.tasksService.updateTaskOrders(this.taskId, ordersToDelete)
          ).subscribe(
            () => {
              this.taskEditorDialogRef.close();
              this.taskEditorDialogRef.afterClosed().subscribe(() => {
                this.tasksService.onEditEvent.emit();
                this.ordersService.onEditEvent.emit();
              });
              this.snackBar.open(
                this.translateService.instant("TASKS.MESSAGES.UPDATE_SUCCESS"),
                "",
                { duration: 5000 }
              );
            },
            () => {
              this.isLoading = false;
              this.snackBar.open(
                this.translateService.instant("TASKS.MESSAGES.UPDATE_ERROR"),
                "",
                { duration: 5000 }
              );
            }
          );
        }
      }
    );
  }

  public fractionSelectOptionChange(index: number, option: MatOption) {
    const control = <FormArray>this.taskExportForm.controls.taskData;
    const tmpSelection = control.controls[index].get("fractions").value;

    if (!tmpSelection.find((i) => i.uuid === option.value.uuid)) {
      control.controls[index]
        .get("fractions")
        .setValue([...tmpSelection, option.value]);
    } else {
      control.controls[index]
        .get("fractions")
        .setValue(tmpSelection.filter((i) => i.uuid !== option.value.uuid));
    }
  }

  private setRangeEndDateMax(date: Date) {
    this.rangeDateEndMin = new Date(
      this.taskExportForm.get("startDate").value || this.todayDate
    );
    this.rangeDateEndMax = new Date(date || this.todayDate);
    this.rangeDateEndMax.setMonth(
      this.rangeDateEndMax.getMonth() !== 11
        ? this.rangeDateEndMax.getMonth() + 1
        : 0
    );
    if (this.rangeDateEndMax.getMonth() === 0) {
      this.rangeDateEndMax.setFullYear(this.rangeDateEndMax.getFullYear() + 1);
    }
    this.rangeDateEndMax.setDate(
      getDaysInMonth(
        this.rangeDateEndMax.getFullYear(),
        this.rangeDateEndMax.getMonth()
      )
    );
  }

  private createTask(newTask: any) {
    const taskForm = this.taskExportForm.getRawValue();
    if (taskForm.recurring) {
      const recurringTask = {
        intervalType: "WEEK",
        intervalValue: taskForm.recurringInfo.intervalValue,
        rangeDateEnd: moment(taskForm.recurringInfo.rangeDateEnd).format(
          "YYYY-MM-DD"
        ),
        weekdays: taskForm.recurringInfo.weekdays,
        task: newTask,
      };
      this.tasksService.createRecurringTask(recurringTask).subscribe(
        () => {
          this.taskEditorDialogRef.close();
          this.taskEditorDialogRef.afterClosed().subscribe(() => {
            this.tasksService.onEditEvent.emit();
            this.ordersService.onEditEvent.emit();
          });
          this.snackBar.open(
            this.translateService.instant("TASKS.MESSAGES.CREATE_SUCCESS"),
            "",
            { duration: 5000 }
          );
        },
        () => {
          this.isLoading = false;
          this.snackBar.open(
            this.translateService.instant("TASKS.MESSAGES.CREATE_ERROR"),
            "",
            { duration: 5000 }
          );
        }
      );
    } else {
      this.tasksService.createTask(newTask).subscribe(
        () => {
          this.taskEditorDialogRef.close();
          this.taskEditorDialogRef.afterClosed().subscribe(() => {
            this.tasksService.onEditEvent.emit();
            this.ordersService.onEditEvent.emit();
          });
          this.snackBar.open(
            this.translateService.instant("TASKS.MESSAGES.CREATE_SUCCESS"),
            "",
            { duration: 5000 }
          );
        },
        () => {
          this.isLoading = false;
          this.snackBar.open(
            this.translateService.instant("TASKS.MESSAGES.CREATE_ERROR"),
            "",
            { duration: 5000 }
          );
        }
      );
    }
  }

  public deleteTask() {
    const deleteDialogRef = this.matDialog.open(ConfirmModalComponent, {
      disableClose: false,
      autoFocus: false,
    });
    deleteDialogRef.componentInstance.confirmMessage =
      this.translateService.instant("TASKS.MESSAGES.DELETE_QUESTION");
    deleteDialogRef.componentInstance.titleMessage =
      this.translateService.instant("TASKS.MESSAGES.DELETE_TASK_TITLE");
    deleteDialogRef.componentInstance.confirmButton =
      this.translateService.instant("TASKS.DELETE");

    deleteDialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const ordersToDelete = [];
        this.task &&
          this.task.taskData &&
          this.task.taskData.forEach((item) => {
            if (item.order && item.order.uuid) {
              ordersToDelete.push(item.order.uuid);
            }
          });

        zip(
          this.tasksService.deleteTask(this.taskId),
          this.tasksService.updateTaskOrders(this.taskId, ordersToDelete)
        ).subscribe(
          () => {
            this.taskEditorDialogRef.close();
            this.taskEditorDialogRef.afterClosed().subscribe(() => {
              this.tasksService.onEditEvent.emit();
              this.ordersService.onEditEvent.emit();
            });
            this.snackBar.open(
              this.translateService.instant("TASKS.MESSAGES.DELETE_SUCCESS"),
              "",
              { duration: 5000 }
            );
          },
          () => {
            this.snackBar.open(
              this.translateService.instant("TASKS.MESSAGES.DELETE_ERROR"),
              "",
              { duration: 5000 }
            );
          }
        );
      }
    });
  }

  public cancelTask() {
    const cancelDialogRef = this.matDialog.open(ConfirmModalComponent, {
      disableClose: false,
      autoFocus: false,
    });
    cancelDialogRef.componentInstance.confirmMessage =
      this.translateService.instant("TASKS.MESSAGES.CANCEL_QUESTION");
    cancelDialogRef.componentInstance.titleMessage =
      this.translateService.instant("TASKS.MESSAGES.CANCEL_TASK_TITLE");
    cancelDialogRef.componentInstance.confirmButton =
      this.translateService.instant("TASKS.CANCEL_TASK");
    cancelDialogRef.componentInstance.skipButton =
      this.translateService.instant("TASKS.CANCEL_SKIP");

    cancelDialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.tasksService.cancelTask(this.taskId).subscribe(
          () => {
            this.taskEditorDialogRef.close();
            this.tasksService.onEditEvent.emit();
            this.ordersService.onEditEvent.emit();
            this.snackBar.open(
              this.translateService.instant("TASKS.MESSAGES.CANCEL_SUCCESS"),
              "",
              { duration: 5000 }
            );
          },
          () => {
            this.snackBar.open(
              this.translateService.instant("TASKS.MESSAGES.CANCEL_ERROR"),
              "",
              { duration: 5000 }
            );
          }
        );
      }
    });
  }

  public completeTask() {
    const completeDialogRef = this.matDialog.open(ConfirmModalComponent, {
      disableClose: false,
      autoFocus: false,
    });
    completeDialogRef.componentInstance.confirmMessage =
      this.translateService.instant("TASKS.MESSAGES.COMPLETE_QUESTION");
    completeDialogRef.componentInstance.titleMessage =
      this.translateService.instant("TASKS.MESSAGES.COMPLETE_TASK_TITLE");
    completeDialogRef.componentInstance.confirmButton =
      this.translateService.instant("TASKS.COMPLETE_TASK");
    completeDialogRef.componentInstance.skipButton =
      this.translateService.instant("TASKS.COMPLETE_SKIP");

    completeDialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.tasksService.completeTask(this.taskId).subscribe(
          () => {
            this.taskEditorDialogRef.close();
            this.tasksService.onEditEvent.emit();
            this.ordersService.onEditEvent.emit();
            this.snackBar.open(
              this.translateService.instant("TASKS.MESSAGES.COMPLETE_SUCCESS"),
              "",
              { duration: 5000 }
            );
          },
          () => {
            this.snackBar.open(
              this.translateService.instant("TASKS.MESSAGES.COMPLETE_ERROR"),
              "",
              { duration: 5000 }
            );
          }
        );
      }
    });
  }

  public setStartDate(newDate) {
    this.taskExportForm.patchValue({ endDate: new Date(newDate) });
    this.setRangeEndDateMax(newDate);
    this.handleRecurringChange();
  }

  public handleRecurringChange() {
    const weekdays = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    const date = this.taskExportForm.get("startDate").value || new Date();

    this.taskExportForm.get("recurringInfo").get("weekdays").patchValue({
      Monday: false,
      Tuesday: false,
      Wednesday: false,
      Thursday: false,
      Friday: false,
      Saturday: false,
      Sunday: false,
    });
    this.taskExportForm
      .get("recurringInfo")
      .get("weekdays")
      .patchValue({
        [weekdays[moment(date).day()]]: true,
      });
  }

  public addOrderSubform(
    index: number,
    orderItem: OrderExportSubform = new OrderExportSubform(),
    generatedFromOrderId = false
  ) {
    if (orderItem.customerCompany && orderItem.customerCompany.uuid) {
      this.getCustomerCompanyBranches(orderItem.customerCompany.uuid);
    }
    Object.keys(this.isExpanded).forEach(
      (item) => (this.isExpanded[item] = false)
    );
    this.isExpanded.push(!orderItem.value);
    this.orderFilter();
    const control = <FormArray>this.taskExportForm.controls.taskData;
    control.push(
      this.formBuilder.group({
        uuid: new FormControl(
          this.mode === "clone"
            ? "other"
            : (generatedFromOrderId && orderItem.uuid) ||
              (orderItem.order && orderItem.order.uuid) ||
              orderItem.orderId ||
              "other"
        ),
        order: new FormControl(
          this.mode === "clone"
            ? { uuid: "other" }
            : (generatedFromOrderId && orderItem) ||
              orderItem.order || { uuid: "other" }
        ),
        customerCompany: new FormControl(
          orderItem.customerCompany || null,
          Validators.required
        ),
        customerCompanyBranch: new FormControl(
          orderItem.customerCompanyBranch
            ? orderItem.customerCompanyBranch.uuid
              ? orderItem.customerCompanyBranch.uuid
              : "other"
            : null,
          Validators.required
        ),
        transport: new FormControl(
          (orderItem.order && orderItem.order.transport) ||
            orderItem.transport ||
            null,
          Validators.required
        ),
        isReady: new FormControl(
          (orderItem.order && orderItem.order.isReady) ||
            orderItem.isReady ||
            false,
          Validators.required
        ),
        kpoEmails: new FormControl(orderItem.kpoEmails || null),
        reinvoice: new FormControl(orderItem.reinvoice || false),
        price: new FormControl(
          orderItem.price ? String(orderItem.price).replace(".", ",") : ""
        ),
        reinvoiceNumber: new FormControl(orderItem.reinvoiceNumber || ""),
        invoiceComment: new FormControl(orderItem.invoiceComment || ""),
        suggestedDate: new FormControl(
          this.mode === "clone"
            ? null
            : (orderItem.order && orderItem.order.suggestedDate) ||
              orderItem.suggestedDate ||
              null
        ),
        fractions: new FormControl(
          orderItem.fractions || null,
          Validators.required
        ),
        value: new FormControl(orderItem.value || null, [
          Validators.required,
          Validators.pattern("[\\d]+[.]{0,1}[\\d]{0,3}"),
        ]),
        unit: new FormControl(orderItem.unit || "kg", Validators.required),
        referenceNumber: new FormControl(
          this.mode === "clone" ? null : orderItem.referenceNumber || null
        ),
        customerCompanyBranchOther: new FormGroup({
          name: new FormControl(
            (orderItem.customerCompanyBranch &&
              orderItem.customerCompanyBranch.name) ||
              null,
            orderItem.customerCompanyBranch &&
            orderItem.customerCompanyBranch.name
              ? Validators.required
              : null
          ),
          street: new FormControl(
            (orderItem.customerCompanyBranch &&
              orderItem.customerCompanyBranch.street) ||
              null,
            Validators.required
          ),
          postalCode: new FormControl(
            (orderItem.customerCompanyBranch &&
              orderItem.customerCompanyBranch.postalCode) ||
              null,
            Validators.required
          ),
          city: new FormControl(
            (orderItem.customerCompanyBranch &&
              orderItem.customerCompanyBranch.city) ||
              null,
            Validators.required
          ),
          country: new FormControl(
            (orderItem.customerCompanyBranch &&
              orderItem.customerCompanyBranch.country) ||
              "Polska",
            Validators.required
          ),
          contact: new FormControl(
            (orderItem.customerCompanyBranch &&
              orderItem.customerCompanyBranch.contact) ||
              null
          ),
          email: new FormControl(
            (orderItem.customerCompanyBranch &&
              orderItem.customerCompanyBranch.email) ||
              null
          ),
          phone: new FormControl(
            (orderItem.customerCompanyBranch &&
              orderItem.customerCompanyBranch.phone) ||
              null,
            Validators.required
          ),
          note: new FormControl(
            (orderItem.customerCompanyBranch &&
              orderItem.customerCompanyBranch.note) ||
              null
          ),
          order_comment: new FormControl(
            orderItem.comment ||
              (orderItem.order && orderItem.order.comment) ||
              null
          ),
          mainBranch: new FormControl(
            (orderItem.customerCompanyBranch &&
              orderItem.customerCompanyBranch.mainBranch) ||
              false
          ),
        }),
      })
    );
    generatedFromOrderId && control.at(index).get("order").disable();
    generatedFromOrderId &&
      setTimeout(() => this.taskExportForm.get("companyBranchId").disable(), 0);

    if (control.at(index).get("reinvoice").value === false) {
      control.at(index).get("price").disable();
      control.at(index).get("reinvoiceNumber").disable();
    }
  }

  public deleteOrderSubform(index: number) {
    this.isExpanded.splice(index, 1);
    const control = <FormArray>this.taskExportForm.controls.taskData;
    const order = control.value[index];
    if (
      order.uuid !== "other" &&
      this.ordersOptionsInitial.includes(order.uuid)
    ) {
      this.ordersService.getOrder(order.uuid).subscribe((res) => {
        this.ordersOptionsRemoved.push(res);
      });
    }
    control.removeAt(index);
  }

  public pickOrder(order: OrderExport, index: number) {
    const control = <FormArray>this.taskExportForm.controls.taskData;
    const previousOrder = control.value[index];
    if (order.customerCompany && order.customerCompany.uuid) {
      this.getCustomerCompanyBranches(order.customerCompany.uuid);
    } else {
      control.controls[index].get("customerCompanyBranch").disable();
    }
    if (order.uuid === "other") {
      control.controls[index].reset();
      control.controls[index].patchValue({
        uuid: "other",
        order: {
          uuid: "other",
        },
        orderData: [],
        customerCompanyBranchOther: {
          country: "Polska",
          mainBranch: false,
        },
      });
    } else {
      let CCBranch =
        order.customerCompanyBranch && order.customerCompanyBranch.uuid;
      if (
        order.customerCompanyBranch &&
        !order.customerCompanyBranch.uuid &&
        order.customerCompanyBranch.street
      ) {
        CCBranch = "other";
      }
      control.controls[index].patchValue({
        uuid: order.uuid || "other",
        order: order || { uuid: "other" },
        customerCompany: order.customerCompany || null,
        fractions: order.fractions || null,
        transport: order.transport || null,
        isReady: order.isReady,
        reinvoice: order.reinvoice,
        kpoEmails: order.kpoEmails,
        price: order.price ? String(order.price).replace(".", ",") : "",
        reinvoiceNumber: order.reinvoiceNumber,
        invoiceComment: order.invoiceComment,
        suggestedDate: order.suggestedDate || null,
        value: order.value || null,
        unit: order.unit || "kg",
        referenceNumber: order.referenceNumber || null,
        customerCompanyBranch: CCBranch,
        customerCompanyBranchOther: {
          name:
            (order.customerCompanyBranch && order.customerCompanyBranch.name) ||
            null,
          street:
            (order.customerCompanyBranch &&
              order.customerCompanyBranch.street) ||
            null,
          postalCode:
            (order.customerCompanyBranch &&
              order.customerCompanyBranch.postalCode) ||
            null,
          city:
            (order.customerCompanyBranch && order.customerCompanyBranch.city) ||
            null,
          country:
            (order.customerCompanyBranch &&
              order.customerCompanyBranch.country) ||
            "Polska",
          contact:
            (order.customerCompanyBranch &&
              order.customerCompanyBranch.contact) ||
            null,
          email:
            (order.customerCompanyBranch &&
              order.customerCompanyBranch.email) ||
            null,
          phone:
            (order.customerCompanyBranch &&
              order.customerCompanyBranch.phone) ||
            null,
          note:
            (order.customerCompanyBranch && order.customerCompanyBranch.note) ||
            null,
          order_comment: order.comment || null,
          mainBranch: false,
        },
      });
    }
    if (order.uuid !== "other") {
      this.ordersOptionsRemoved = this.ordersOptionsRemoved.filter(
        (item) => item.uuid !== order.uuid
      );
    }
    if (
      previousOrder &&
      this.ordersOptionsInitial.includes(previousOrder.uuid)
    ) {
      this.ordersService.getOrder(previousOrder.uuid).subscribe((res) => {
        this.ordersOptionsRemoved.push(res);
      });
    }
  }

  public async onChangeCustomerCompany(
    company: FilteredCompany,
    index: number
  ) {
    if (company && company.uuid) {
      await this.getCustomerCompanyBranches(company.uuid);
    }
    const control = <FormArray>this.taskExportForm.controls.taskData;
    if (this.taskExportForm.get("taskData").value[index].uuid !== "other") {
      control.controls[index].patchValue({
        customerCompany: company,
        uuid: "other",
        order: { uuid: "other" },
        fractions: null,
        value: null,
        unit: "kg",
      });
    }
    const ccMainBranch =
      this.customerCompanyBranches[company && company.uuid] &&
      this.customerCompanyBranches[company && company.uuid][0];
    control.controls[index].patchValue({
      customerCompany: company,
      customerCompanyBranch: ccMainBranch ? ccMainBranch.uuid || "other" : null,
      customerCompanyBranchOther: {
        name: (ccMainBranch && ccMainBranch.name) || null,
        street: (ccMainBranch && ccMainBranch.street) || null,
        postalCode: (ccMainBranch && ccMainBranch.postalCode) || null,
        city: (ccMainBranch && ccMainBranch.city) || null,
        country: (ccMainBranch && ccMainBranch.country) || "Polska",
        contact: (ccMainBranch && ccMainBranch.contact) || null,
        email: (ccMainBranch && ccMainBranch.email) || null,
        phone: (ccMainBranch && ccMainBranch.phone) || null,
        note: (ccMainBranch && ccMainBranch.note) || null,
        mainBranch: false,
      },
    });
  }

  public clearSearch(searchField: string) {
    this[searchField].setValue("");
  }

  public compareObjectsByUuid(val1, val2): boolean {
    return val1 && val2 && val1.uuid && val2.uuid && val1.uuid === val2.uuid;
  }

  public addCustomerCompany(index: number) {
    const editorDialogRef = this.matDialog.open(CompanyEditorComponent, {
      panelClass: "company-editor-dialog",
      autoFocus: false,
      disableClose: true,
      data: {
        mode: "add",
      },
    });

    editorDialogRef.componentInstance.onAddCompany.subscribe((res) => {
      this.customerCompanyOptions.push(res);
      this.onChangeCustomerCompany(res, index);
      this.companySelect.close();
    });
  }

  public getTitle(): string {
    switch (this.mode) {
      case "add":
      case "clone":
        return this.translateService.instant("TASKS.TITLE_ADD");
      case "edit":
        return this.translateService.instant("TASKS.TITLE_EDIT");
      case "view":
        return this.translateService.instant("TASKS.TITLE_VIEW");
      default:
        return;
    }
  }

  public showHistory() {
    this.matDialog.open(HistoryDialogComponent, {
      panelClass: "history-dialog",
      autoFocus: false,
      disableClose: true,
      data: {
        item: this.task,
        itemOrigin: ItemsOrigins.Task,
        itemId: this.taskId,
        itemNumber: this.task.taskNumber,
      },
    });
  }

  public enableEditMode() {
    this.indexStep = this.stepper.selectedIndex;
    this.taskEditorDialogRef.close();
    this.matDialog.open(TaskExportFormComponent, {
      panelClass: "task-editor-dialog",
      autoFocus: false,
      disableClose: true,
      data: {
        mode: "edit",
        taskId: this.taskId,
        indexStep: this.indexStep,
      },
    });
  }

  public enableCloneMode() {
    this.indexStep = this.stepper.selectedIndex;
    this.taskEditorDialogRef.close();
    this.matDialog.open(TaskExportFormComponent, {
      panelClass: "task-editor-dialog",
      autoFocus: false,
      disableClose: true,
      data: {
        mode: "clone",
        taskId: this.taskId,
        indexStep: this.indexStep,
      },
    });
  }

  public getOrdersOptions(index: number) {
    const options = [...this.ordersOptions, ...this.ordersOptionsRemoved];
    const selectedOrder = this.taskExportForm.get("taskData").value[index];
    this.taskExportForm.get("taskData").value &&
      this.taskExportForm.get("taskData").value.forEach((chosenOrder) => {
        if (
          chosenOrder.uuid &&
          chosenOrder.uuid !== "other" &&
          options.findIndex((item) => item.uuid === chosenOrder.uuid) > -1 &&
          chosenOrder.uuid !== selectedOrder.uuid
        ) {
          options.splice(
            options.findIndex((item) => item.uuid === chosenOrder.uuid),
            1
          );
        }
      });
    if (
      selectedOrder.uuid &&
      options.findIndex((order) => order.uuid === selectedOrder.uuid) === -1 &&
      selectedOrder.uuid !== "other"
    ) {
      options.push(selectedOrder.order || selectedOrder);
    }
    return options;
  }

  public getCustomerCompaniesOptions(index: number) {
    const options = [...this.customerCompanyOptions];
    const selectedOrder = this.taskExportForm.get("taskData").value[index];
    if (
      selectedOrder.customerCompany &&
      selectedOrder.customerCompany.uuid &&
      options.findIndex(
        (item) => item.uuid === selectedOrder.customerCompany.uuid
      ) === -1
    ) {
      options.push(selectedOrder.customerCompany);
    }
    return options;
  }

  public getTransportOptions() {
    const options = [...this.transportOptions];
    const chosenTransport = this.taskExportForm.get("transport").value;
    if (
      chosenTransport &&
      options.findIndex((item) => item.uuid === chosenTransport.uuid) === -1
    ) {
      options.push(chosenTransport);
    }
    return options;
  }

  public getFractionsOptions(index: number) {
    const options = [...this.fractionOptions];
    const selectedOrder = this.taskExportForm.get("taskData").value[index];
    selectedOrder.fractions &&
      selectedOrder.fractions.forEach((fraction) => {
        if (
          !this.searchFractions.value &&
          options.findIndex((item) => item.uuid === fraction.uuid) === -1
        ) {
          options.push(fraction);
        }
      });
    return options;
  }

  public reinvoiceChange(reinvoiceChecked: boolean, orderIndex: number) {
    const control = <FormArray>this.taskExportForm.controls.taskData;
    if (reinvoiceChecked) {
      control.controls[orderIndex].get("price").enable();
      control.controls[orderIndex].get("reinvoiceNumber").enable();
    } else {
      control.controls[orderIndex].get("price").setValue(null);
      control.controls[orderIndex].get("price").disable();
      control.controls[orderIndex].get("reinvoiceNumber").setValue("");
      control.controls[orderIndex].get("reinvoiceNumber").disable();
    }
  }

  public openEmailClient(index: number) {
    const taskData = this.taskExportForm.getRawValue();
    if (taskData.taskData[index].customerCompanyBranchOther.email) {
      return `mailto:${taskData.taskData[index].customerCompanyBranchOther.email}`;
    }
  }

  public canSendEmail(index: number) {
    return (
      this.mode === "view" &&
      this.taskExportForm.getRawValue().taskData[index]
        .customerCompanyBranchOther.email
    );
  }

  private startEndDateTimezoneValid(start, end) {
    if (this.mode === "view") {
      //we need to display current values even if they are wrong in `view` mode
      if (start && !this.startTimeOptions.includes(start)) {
        this.startTimeOptions = [start, ...this.startTimeOptions];
      }
      if (end && !this.endTimeOptions.includes(end)) {
        this.endTimeOptions = [end, ...this.endTimeOptions];
      }
      return true;
    } else {
      // if at least one of current values is wrong we clear fields
      if (start && !this.startTimeOptions.includes(start)) {
        return false;
      }
      if (end && !this.endTimeOptions.includes(end)) {
        return false;
      }
      return true;
    }
  }

  private createForm(inputData: TaskExport = new TaskExport()) {
    inputData.startTime = inputData.startDate
      ? moment(new Date(inputData.startDate)).format("HH:mm")
      : null;
    inputData.endTime = inputData.endDate
      ? moment(new Date(inputData.endDate)).format("HH:mm")
      : null;
    if (
      !this.startEndDateTimezoneValid(inputData.startTime, inputData.endTime)
    ) {
      inputData.startTime = null;
      inputData.endTime = null;
    }
    this.taskExportForm = this.formBuilder.group(
      {
        name: new FormControl(inputData.name || null, Validators.required),
        companyBranchId: new FormControl(
          (inputData.companyBranch && inputData.companyBranch.uuid) ||
            this.data.companyBranchId ||
            null,
          Validators.required
        ),
        transport: new FormControl(
          inputData.transport || null,
          Validators.required
        ),
        startDate: new FormControl(
          this.mode === "clone"
            ? null
            : inputData.startDate || this.data.startDate || null,
          Validators.required
        ),
        startTime: new FormControl(
          inputData.startTime || this.data.startTime || null,
          Validators.required
        ),
        endDate: new FormControl(
          this.mode === "clone"
            ? null
            : inputData.endDate || this.data.endDate || null,
          Validators.required
        ),
        endTime: new FormControl(
          inputData.endTime || this.data.endTime || null,
          Validators.required
        ),
        taskType: new FormControl(
          inputData.taskType || this.taskTypes.Export,
          Validators.required
        ),
        comment: new FormControl(
          this.mode === "add" || this.mode === "clone"
            ? null
            : inputData.comment || null
        ),
        referenceNumber: new FormControl(
          this.mode === "add" || this.mode === "clone"
            ? null
            : inputData.referenceNumber || null
        ),
        emailService: new FormControl(
          this.mode === "add" || this.mode === "clone"
            ? true
            : inputData.emailService
        ),
        registrationNumFront: new FormControl(
          this.mode === "add"
            ? null
            : inputData.forwarderRegistrationNumberFront
            ? inputData.forwarderRegistrationNumberFront
            : null
        ),
        registrationNumBack: new FormControl(
          this.mode === "add"
            ? null
            : inputData.forwarderRegistrationNumberBack
            ? inputData.forwarderRegistrationNumberBack
            : null
        ),
        taskData: this.formBuilder.array([], Validators.required),
        recurring: new FormControl(false),
        recurringInfo: this.formBuilder.group({
          intervalType: new FormControl("WEEK"),
          intervalValue: new FormControl(1, Validators.pattern(/\d/)),
          rangeDateEnd: new FormControl(inputData.startDate),
          weekdays: new FormGroup({
            Monday: new FormControl(false),
            Tuesday: new FormControl(false),
            Wednesday: new FormControl(false),
            Thursday: new FormControl(false),
            Friday: new FormControl(false),
            Saturday: new FormControl(false),
            Sunday: new FormControl(false),
          }),
        }),
        forwarderId: new FormControl(inputData.forwarderId || ""),
        forwarderCompanyName: new FormControl(
          inputData.forwarderCompanyName || ""
        ),
        originalCompanyName: new FormControl(
          inputData.originalCompanyName || ""
        ),
        forwarderDriverName: new FormControl(
          inputData.forwarderDriverName || ""
        ),
        forwarderDriverPhone: new FormControl(
          inputData.forwarderDriverPhone || ""
        ),
        forwarderRegistrationNumberFront: new FormControl(
          inputData.forwarderRegistrationNumberFront || ""
        ),
        forwarderRegistrationNumberBack: new FormControl(
          inputData.forwarderRegistrationNumberBack || ""
        ),
        forwarderKilometers: new FormControl(
          inputData.forwarderKilometers
            ? String(inputData.forwarderKilometers).replace(".", ",")
            : ""
        ),
        forwarderRouteDescription: new FormControl(
          inputData.forwarderRouteDescription || ""
        ),
        forwarderBdo: new FormControl(inputData.forwarderBdo || ""),
        forwarderCarName: new FormControl(inputData.forwarderCarName || ""),
        forwarderCarType: new FormControl(inputData.forwarderCarType || ""),
        forwarderExpense: new FormControl(
          inputData.forwarderExpense
            ? String(inputData.forwarderExpense).replace(".", ",")
            : ""
        ),
        forwarderInvoiceNumber: new FormControl(
          inputData.forwarderInvoiceNumber || ""
        ),
      },
      {
        validator: StartEndDateValidator.CheckDates,
      }
    );
    inputData.taskData &&
      inputData.taskData.forEach((item, index) =>
        this.addOrderSubform(index, item)
      );
    this.setRangeEndDateMax(this.taskExportForm.get("startDate").value);
    this.taskExportForm.get("registrationNumFront").disable();
    this.taskExportForm.get("registrationNumBack").disable();

    if (this.mode === "add" || this.mode === "clone") {
      this.taskExportForm.get("companyBranchId").disable();
    }
    this.searchForwarder.setValue(inputData.originalCompanyName || "");
    if (this.mode === "view") {
      this.taskExportForm.disable();
    }
    if (this.mode === "edit") {
      this.getCompanyCars(inputData.forwarderId);
    }
  }

  private createSearchFilters() {
    this.searchCustomerCompany = new FormControl("");
    this.searchTransport = new FormControl("");
    this.searchFractions = new FormControl("");
    this.searchOrders = new FormControl("");
    this.searchForwarder = new FormControl("");
    this.companyCarSelect = new FormControl("");
    this.forwarderIdSelect = new FormControl("");

    this.searchCustomerCompany.valueChanges
      .debounceTime(300)
      .subscribe((value) => {
        this.filteredListsService
          .getCompaniesList({ ...this.filterParams, ...{ search: value } })
          .subscribe((response) => {
            this.customerCompanyOptions = response.customerCompanies;
          });
      });
    this.searchTransport.valueChanges.debounceTime(300).subscribe((value) => {
      this.filteredListsService
        .getTransportList({ ...this.filterParamsFullList, ...{ name: value } })
        .subscribe((response) => {
          this.transportOptions = response.transports;
        });
    });
    this.searchFractions.valueChanges.debounceTime(300).subscribe((value) => {
      this.filteredListsService
        .getFractionsList({
          ...this.filterParamsFullList,
          ...{ search: value },
        })
        .subscribe((response) => {
          this.fractionOptions = response.fractions;
        });
    });
    this.searchOrders.valueChanges.debounceTime(300).subscribe((value) => {
      this.filteredListsService
        .getOrdersList({
          ...this.filterOrdersParams,
          ...{
            search: value,
            companyBranchUuid: this.taskExportForm.value.companyBranchId,
          },
        })
        .subscribe((response) => {
          this.ordersOptions = <OrderExport[]>response.orders;
        });
    });
    this.searchForwarder.valueChanges.debounceTime(300).subscribe((value) => {
      this.filteredListsService
        .getCompaniesList({
          ...this.filterForwarderParams,
          ...{ search: value },
        })
        .subscribe((response) => {
          this.forwarderCompanies = response.customerCompanies;
        });
    });
    this.forwarderIdSelect.valueChanges.subscribe((value) => {
      this.companyCarsService
        .getCompanyCarsList(this.filterParamsFullList, value.uuid)
        .subscribe((response) => {
          this.companyCars = response.cars;
          this.taskExportForm.patchValue({
            forwarderId: value.uuid,
            forwarderCompanyName: value.name,
            forwarderDriverName: null,
            forwarderDriverPhone: null,
            forwarderRegistrationNumberFront: null,
            forwarderRegistrationNumberBack: null,
            forwarderBdo: null,
            forwarderCarName: null,
            forwarderCarType: null,
          });
        });
    });

    this.companyCarSelect.valueChanges.subscribe((companyCar) => {
      this.taskExportForm.patchValue({
        forwarderCompanyName: companyCar.companyName,
        forwarderDriverName: companyCar.driverName,
        forwarderDriverPhone: companyCar.driverPhone,
        forwarderRegistrationNumberFront: companyCar.registrationNumberFront,
        forwarderRegistrationNumberBack: companyCar.registrationNumberBack,
        forwarderBdo: companyCar.bdo,
        forwarderCarName: companyCar.name,
        forwarderCarType: companyCar.type,
      });
    });
  }

  public orderFilter() {
    this.filteredListsService
      .getOrdersList({
        ...this.filterOrdersParams,
        ...{ companyBranchUuid: this.taskExportForm.value.companyBranchId },
      })
      .subscribe((response) => {
        this.ordersOptions = <OrderExport[]>response.orders;
      });
  }

  private createNewOrders(
    taskData: OrderExportSubform[],
    formCompanyBranchId: string
  ): Observable<OrderExportSubform[]> {
    const taskDataWithOtherType = taskData.filter(
      (task) => task.uuid === "other"
    );
    if (!taskDataWithOtherType.length) {
      return of([]);
    }
    let tmpCompanyBranchId;
    if (this.data.companyBranchId) {
      tmpCompanyBranchId = this.data.companyBranchId;
    } else if (
      this.task &&
      this.task.companyBranch &&
      this.task.companyBranch.uuid
    ) {
      tmpCompanyBranchId = this.task.companyBranch.uuid;
    } else if (this.task && this.task.companyBranchId) {
      tmpCompanyBranchId = this.task.companyBranchId;
    } else {
      tmpCompanyBranchId = formCompanyBranchId;
    }
    const observables = taskDataWithOtherType.map((item) => {
      if (item.customerCompanyBranch.uuid === "other") {
        delete item.customerCompanyBranch.uuid;
      }
      let tmpCompanyBranchId;
      if (this.data.companyBranchId) {
        tmpCompanyBranchId = this.data.companyBranchId;
      } else if (
        this.task &&
        this.task.companyBranch &&
        this.task.companyBranch.uuid
      ) {
        tmpCompanyBranchId = this.task.companyBranch.uuid;
      } else if (this.task && this.task.companyBranchId) {
        tmpCompanyBranchId = this.task.companyBranchId;
      } else {
        tmpCompanyBranchId = formCompanyBranchId;
      }
      const newOrder = {
        companyBranchId: tmpCompanyBranchId,
        customerCompanyId: item.customerCompany.uuid,
        salesAccepted: true,
        customerCompanyBranch: item.customerCompanyBranch,
        referenceNumber: item.referenceNumber,
        transportId: this.taskExportForm.get("transport").value.uuid,
        fractionsId:
          (item.fractions && item.fractions.map((fraction) => fraction.uuid)) ||
          [],
        unit: item.unit || "kg",
        value: item.value,
        suggestedDate:
          moment(item.suggestedDate).format("YYYY-MM-DD") ||
          moment(this.taskExportForm.get("startDate").value).format(
            "YYYY-MM-DD"
          ) ||
          moment().format("YYYY-MM-DD"),
        orderType: OrderTypes.Export,
        isReady: item.isReady,
        reinvoice: item.reinvoice,
        kpoEmails: item.kpoEmails,
        price: Number(String(item.price).replace(",", ".")),
        reinvoiceNumber: item.reinvoiceNumber,
        invoiceComment: item.invoiceComment,
      };
      return this.ordersService.createOrder(newOrder).pipe(
        map((resOrder: OrderExport) => {
          return {
            uuid: resOrder.uuid,
            customerCompany: resOrder.customerCompany,
            customerCompanyBranch: resOrder.customerCompanyBranch,
            referenceNumber: resOrder.referenceNumber || null,
            fractions: resOrder.fractions,
            transport: resOrder.transport,
            isReady: resOrder.isReady,
            reinvoice: resOrder.reinvoice,
            kpoEmails: resOrder.kpoEmails,
            price: resOrder.price,
            reinvoiceNumber: resOrder.reinvoiceNumber,
            invoiceComment: resOrder.invoiceComment,
            suggestedDate: item.suggestedDate,
            unit: resOrder.unit,
            value: resOrder.value,
          };
        })
      );
    });
    return zip(...observables);
  }

  private mapTaskData(taskData: OrderExportSubform[]) {
    taskData = taskData.map((item) => {
      const taskDataItem = {
        customerCompanyBranch: item.customerCompanyBranch,
        customerCompanyId: item.customerCompany
          ? item.customerCompany.uuid
          : null,
        referenceNumber: item.referenceNumber || null,
        transport: item.transport,
        suggestedDate: item.suggestedDate,
        isReady: item.isReady,
        reinvoice: item.reinvoice,
        kpoEmails: item.kpoEmails,
        price: Number(String(item.price).replace(",", ".")) || null,
        reinvoiceNumber: item.reinvoiceNumber,
        invoiceComment: item.invoiceComment,
        order: item.order,
        fractionsIds:
          (item.fractions && item.fractions.map((fraction) => fraction.uuid)) ||
          [],
        unit: item.unit || "kg",
        value: item.value,
      };
      if (item.uuid !== "other") {
        taskDataItem["orderId"] = item.uuid;
      }
      return taskDataItem;
    });
    return taskData;
  }

  public changeStep(event) {
    this.indexStep = event.selectedIndex;
  }

  public prevStep(event) {
    this.indexStep = this.indexStep - 1 >= 0 ? this.indexStep - 1 : 0;
  }

  public handleCCBranchChange(branchId: string, i: number) {
    const control = <FormArray>this.taskExportForm.controls.taskData;
    const group = <FormGroup>control.controls[i];
    const companyId =
      group.get("customerCompany").value &&
      group.get("customerCompany").value.uuid;
    const branch =
      companyId &&
      this.customerCompanyBranches[companyId] &&
      this.customerCompanyBranches[companyId].find(
        (item: CustomerCompanyBranch) => item.uuid === branchId
      );
    group.get("customerCompanyBranchOther").patchValue({
      name: (branch && branch.name) || null,
      street: (branch && branch.street) || null,
      postalCode: (branch && branch.postalCode) || null,
      city: (branch && branch.city) || null,
      country: (branch && branch.country) || "Polska",
      contact: (branch && branch.contact) || null,
      email: (branch && branch.email) || null,
      phone: (branch && branch.phone) || null,
      note: (branch && branch.note) || null,
      mainBranch: false,
    });
  }

  private async getCustomerCompanyBranches(customerCompanyId: string) {
    try {
      const res = await this.companyBranchesService
        .getCompanyBranchesList({ limit: 500, page: 0 }, customerCompanyId)
        .toPromise();
      this.customerCompanyBranches[customerCompanyId] = res.branches;
      return true;
    } catch (e) {
      return false;
    }
  }

  public getFormCompanyUuid(i: number): string {
    const control = <FormArray>this.taskExportForm.controls.taskData;
    const group = <FormGroup>control.controls[i];
    return (
      (group.get("customerCompany").value &&
        group.get("customerCompany").value.uuid) ||
      "-"
    );
  }

  public getTooltip(branch: any) {
    return `${branch.name}
    ${branch.street}
    ${branch.postalCode} ${branch.city}`;
  }

  public getTooltipCompany(company: any) {
    if (company) {
      return `${company.name} 
      ${company.address.street}
      ${company.address.postalCode} ${company.address.city}`;
    }
  }

  private getCompanyCars(forwarderId: string) {
    this.companyCarsService
      .getCompanyCarsList(this.filterParamsFullList, forwarderId)
      .subscribe((response) => {
        this.companyCars = response.cars;
      });
  }

  public selected(event: MatAutocompleteSelectedEvent): void {
    this.searchForwarder.patchValue(event.option.viewValue);
    this.companyCarsService
      .getCompanyCarsList(this.filterParamsFullList, event.option.value)
      .subscribe((response) => {
        this.companyCars = response.cars;
        this.taskExportForm.patchValue({
          forwarderId: event.option.value,
          forwarderCompanyName: event.option.viewValue,
          forwarderDriverName: null,
          forwarderDriverPhone: null,
          forwarderRegistrationNumberFront: null,
          forwarderRegistrationNumberBack: null,
          forwarderBdo: null,
          forwarderCarName: null,
          forwarderCarType: null,
        });
      });
  }

  private getPdfNeededData() {
    this.usersService.getCompanyBranchList().subscribe((res) => {
      this.pdfBranch = res.branches.find(
        (item) => item.uuid === this.task.companyBranch.uuid
      );
    });
    if (this.task.forwarderId) {
      this.companiesService
        .getCompany(this.task.forwarderId)
        .subscribe((res) => {
          this.pdfForwarder = res;
        });
    } else {
      this.pdfForwarder = { name: "Brak Danych", address: "Brak danych" };
    }
  }
  public resendNotification(index: number) {
    const orderId = this.task.taskData[index].order.uuid;
    this.isLoading = true;
    this.ordersService.resendNotification(orderId).subscribe(
      () => {
        this.isLoading = false;
        this.snackBar.open(
          this.translateService.instant("TASKS.MESSAGES.NOTIFICATION_SENT"),
          "",
          { duration: 5000 }
        );
      },
      () => {
        this.isLoading = false;
        this.snackBar.open(
          this.translateService.instant("TASKS.MESSAGES.ERROR"),
          "",
          { duration: 5000 }
        );
      }
    );
  }
}
